import { Alert, Container, Header, Spinner } from '@amzn/awsui-components-react';

import { UserPermissions } from "../../services/user-permissions";
import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import { getString } from '../ui-string-labels/ui-strings-utils';
import { useCatalogicalConfig } from "hooks/useCatalogicalConfig";
import { Building, FinanceCode, CATALOGICAL_DOMAINS } from "interfaces/catalogical-config";
import { PERMISSIONS } from "configuration/config";

type ContainerProps = {
    children: React.ReactNode;
};

/**
 * Shows a loading spinner during initial Authentication/Authorization.
 */
const AuthLoading: FunctionComponent<ContainerProps> = (props: ContainerProps) => {
    const [authFailed, setAuthFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined as Error | undefined);

    // Preload Catalogical config data into cache (only after auth, respecting feature flag)
    const catalogicalEnabled = !loading && UserPermissions.instance.hasPermission(PERMISSIONS.USE_CATALOGICAL_CONFIG);
    useCatalogicalConfig<Building>(catalogicalEnabled ? CATALOGICAL_DOMAINS.BUILDINGS : "");
    useCatalogicalConfig<FinanceCode>(catalogicalEnabled ? CATALOGICAL_DOMAINS.FINANCE_CODES : "");

    useEffect(() => {
        if (!UserPermissions.instance.hasAuthorized && !authFailed) {
            UserPermissions.instance
                .authorize()
                .catch((err) => {
                    setError(err);
                    setAuthFailed(true);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    });

    if (error) {
        return (
            <Alert
                statusIconAriaLabel="Error"
                type="error"
                header={getString("common.authLoading.failedMessage")}
                data-testid="auth-error"
            >
                {error.message}
            </Alert>

        );
    }

    if (loading) {
        return (
            <Container>
                <Header
                    variant="h2"
                    data-testid="auth-loading"
                >
                    {getString("common.authLoading.loadingMessage")}
                </Header>
                <Spinner size='large' />
            </Container>
        );
    }
    return <Fragment>{props.children}</Fragment>
};

export default AuthLoading;
